import { useCallback, useState } from 'react';
import type { ContextMenuItem, ContextMenuParams } from '@core/components';
import { useConfirmDialog } from './useConfirmDialog';

export interface UseCrudListActionsOptions<T> {
  items: T[];
  load: () => Promise<void>;
  deleteItem?: (item: T) => Promise<void>;
  deleteSuccessMessage?: string;
  deleteErrorMessage?: string;
}

export function useCrudListActions<T extends { id: number }>({
  items,
  load,
  deleteItem,
  deleteSuccessMessage,
  deleteErrorMessage,
}: UseCrudListActionsOptions<T>) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);

  const openCreate = useCallback(() => {
    setEditingItem(null);
    setDialogOpen(true);
  }, []);

  const openEdit = useCallback((item: T) => {
    setEditingItem(item);
    setDialogOpen(true);
  }, []);

  const closeDialog = useCallback(() => {
    setEditingItem(null);
    setDialogOpen(false);
  }, []);

  const {
    item: deleteConfirm,
    request: requestDelete,
    cancel: cancelDelete,
    confirm: confirmDelete,
    loading: deleteLoading,
  } = useConfirmDialog<T>({
    onConfirm: (item) => deleteItem?.(item),
    onSuccess: load,
    successMessage: deleteSuccessMessage,
    errorMessage: deleteErrorMessage,
  });

  const getRowId = useCallback((item: T) => String(item.id), []);

  const findByRowId = useCallback(
    (rowId: string) => items.find((item) => getRowId(item) === rowId),
    [items, getRowId]
  );

  const contextMenuItems = useCallback(
    ({ rowId }: ContextMenuParams): ContextMenuItem[] => [
      {
        label: 'Edit',
        onClick: () => {
          const item = findByRowId(rowId);
          if (item) {
            openEdit(item);
          }
        },
      },
      ...(deleteItem
        ? [
            {
              label: 'Delete',
              destructive: true,
              onClick: () => {
                const item = findByRowId(rowId);
                if (item) {
                  requestDelete(item);
                }
              },
            },
          ]
        : []),
    ],
    [findByRowId, openEdit, deleteItem, requestDelete]
  );

  return {
    dialogOpen,
    editingItem,
    openCreate,
    openEdit,
    closeDialog,
    deleteConfirm,
    requestDelete,
    cancelDelete,
    confirmDelete,
    deleteLoading,
    getRowId,
    findByRowId,
    contextMenuItems,
  };
}
